import React from "react";
import { useSelector } from "react-redux";
import { PlusCircle, ArrowUpRight } from "lucide-react";
import useThemeHook from "../hooks/useThemeHook";
import {
  FIRST_DEPOSIT_MIN_PI,
  TURBO_DEPOSIT_MIN_PI,
} from "../utils/rewardTables";

interface DepositCardProps {
  onClick: () => void;
}

/**
 * Home deposit card. The copy only mentions what `completePayment` credits:
 * a first-deposit Flash Boost and the ≥100 π Turbo.
 */
const DepositCard: React.FC<DepositCardProps> = ({ onClick }) => {
  const theme = useThemeHook();
  const user = useSelector((state: any) => state?.actualUser?.user);

  const isDark = theme === "dark";
  const isFirstDeposit = !((user?.totalPiDeposited ?? 0) > 0);

  const subtitle = isFirstDeposit
    ? `First deposit (${FIRST_DEPOSIT_MIN_PI} π+) unlocks Flash Boost — 10x grants for 24h`
    : `Deposit ${TURBO_DEPOSIT_MIN_PI} π+ → Turbo — 5x grants for 24h`;

  return (
    <div className="px-1 mb-2">
      <button
        onClick={onClick}
        className={`w-full relative overflow-hidden rounded-3xl p-6 transition-all active:scale-[0.98] group shadow-xl ${
          isDark
            ? "bg-gradient-to-br from-indigo-900 via-indigo-950 to-purple-900 border border-indigo-500/30 shadow-indigo-500/10"
            : "bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-600 text-white shadow-indigo-600/30"
        }`}
      >
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform duration-700 ease-out">
          <PlusCircle size={140} strokeWidth={1} />
        </div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-3xl" />

        <div className="flex items-center justify-between relative z-10 gap-4">
          <div className="flex items-center gap-5 min-w-0">
            <div
              className={`flex items-center justify-center w-16 h-16 rounded-2xl shadow-inner border shrink-0 ${
                isDark
                  ? "bg-indigo-500/20 border-indigo-400/20 shadow-black/20"
                  : "bg-white/20 border-white/40 shadow-indigo-800/20"
              }`}
            >
              <PlusCircle size={36} className={isDark ? "text-indigo-400" : "text-white"} />
            </div>

            <div className="text-left min-w-0">
              <h3 className="font-extrabold text-2xl tracking-tight mb-1">Deposit Pi</h3>
              <p
                className={`text-xs font-semibold leading-snug ${
                  isDark ? "text-indigo-300/80" : "text-indigo-100"
                }`}
              >
                {subtitle}
              </p>
            </div>
          </div>

          <div
            className={`w-10 h-10 flex items-center justify-center rounded-full border shrink-0 transition-all duration-300 group-hover:bg-white/10 ${
              isDark ? "border-indigo-500/30 text-indigo-400" : "border-white/40 text-white"
            }`}
          >
            <ArrowUpRight size={24} />
          </div>
        </div>
      </button>
    </div>
  );
};

export default DepositCard;
